import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSocialMediaContentService } from '../../hooks/useSocialMediaContentService';
import type {
    SocialMediaContent,
    SocialMediaAnalyticsOverview,
    SocialOAuthConnection,
    SocialPublishStatusResponse,
    SocialConnectPlatformRow
} from '../../services/socialMediaContentService';
import { SOCIAL_CONNECT_PLATFORMS, SOCIAL_PLATFORM_LABELS, platformDisplayLabel } from '../../services/socialMediaContentService';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend
} from 'recharts';
import ViewContentModal from './ViewContentModal';
import './AdminComponents.css';

const SocialMediaContentManagement: React.FC = () => {
    const [content, setContent] = useState<SocialMediaContent[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedContent, setSelectedContent] = useState<SocialMediaContent | null>(null);
    const [platform, setPlatform] = useState<string>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState<'all' | 'draft' | 'pending' | 'approved' | 'published' | 'paused' | 'stopped' | 'analytics' | 'accounts'>('all');
    const [connections, setConnections] = useState<SocialOAuthConnection[]>([]);
    const [publishStatusByContent, setPublishStatusByContent] = useState<Record<number, SocialPublishStatusResponse>>({});
    const [publishStatusLoadingId, setPublishStatusLoadingId] = useState<number | null>(null);
    const [publishStatusErrorByContent, setPublishStatusErrorByContent] = useState<Record<number, string>>({});
    const [publishModes, setPublishModes] = useState<Record<string, 'simulated' | 'live'>>({});
    const [connectPlatformRows, setConnectPlatformRows] = useState<SocialConnectPlatformRow[]>([]);
    const [newCustomPlatformCode, setNewCustomPlatformCode] = useState('');
    const [newCustomDisplayLabel, setNewCustomDisplayLabel] = useState('');
    const [newCustomProviderKey, setNewCustomProviderKey] = useState('');
    const [analytics, setAnalytics] = useState<SocialMediaAnalyticsOverview | null>(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [rangePreset, setRangePreset] = useState<'7d' | '30d' | 'custom'>('7d');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [analyticsPlatform, setAnalyticsPlatform] = useState<'all' | string>('all');
    const [hiddenSeries, setHiddenSeries] = useState<Record<string, boolean>>({
        created: false,
        approved: false,
        running: false,
        paused: false,
        stopped: false
    });

    const navigate = useNavigate();
    const contentService = useSocialMediaContentService();

    const loadContent = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await contentService.getAllContent();
            if (response.success && response.data) {
                setContent(response.data);
            } else {
                throw new Error(response.error || 'Failed to load content');
            }
        } catch (err) {
            console.error('Error loading content:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to load content. Please try again.';
            setError(errorMessage);
            
            if (err instanceof Error && 
                (err.message.toLowerCase().includes('auth') || 
                err.message.toLowerCase().includes('unauthorized'))) {
                navigate('/admin/login');
            }
        } finally {
            setLoading(false);
        }
    }, [contentService, navigate]);

    const loadAnalytics = useCallback(async () => {
        try {
            setAnalyticsLoading(true);
            const response = await contentService.getAnalyticsOverview(
                rangePreset === 'custom'
                    ? { fromDate: fromDate || undefined, toDate: toDate || undefined, platform: analyticsPlatform }
                    : { days: rangePreset === '7d' ? 7 : 30, platform: analyticsPlatform }
            );
            if (response.success && response.data) {
                setAnalytics(response.data);
            }
        } catch (err) {
            console.error('Error loading analytics:', err);
        } finally {
            setAnalyticsLoading(false);
        }
    }, [contentService, rangePreset, fromDate, toDate, analyticsPlatform]);

    const loadConnections = useCallback(async () => {
        const listRes = await contentService.listConnectPlatforms();
        let rows: SocialConnectPlatformRow[] = [];
        if (listRes.success && listRes.data && listRes.data.length > 0) {
            rows = listRes.data;
        } else {
            rows = SOCIAL_CONNECT_PLATFORMS.map((p) => ({
                platformCode: p,
                displayLabel: SOCIAL_PLATFORM_LABELS[p],
                providerKey: `social_${p.toLowerCase()}`,
                builtin: true
            }));
        }
        setConnectPlatformRows(rows);

        const response = await contentService.getConnections();
        if (response.success && response.data) {
            setConnections(response.data);
        }

        const modeResults = await Promise.all(rows.map((r) => contentService.getPlatformPublishMode(r.platformCode)));
        setPublishModes((prev) => {
            const next = { ...prev };
            rows.forEach((row, index) => {
                const res = modeResults[index];
                if (res.success && res.data) {
                    next[row.platformCode] = res.data;
                } else if (next[row.platformCode] === undefined) {
                    next[row.platformCode] = 'simulated';
                }
            });
            return next;
        });
    }, [contentService]);

    useEffect(() => {
        const saved = localStorage.getItem('socialMediaAnalyticsHiddenSeries');
        if (!saved) return;
        try {
            const parsed = JSON.parse(saved);
            setHiddenSeries(prev => ({ ...prev, ...parsed }));
        } catch {
            // ignore malformed localStorage value
        }
    }, []);

    useEffect(() => {
        localStorage.setItem('socialMediaAnalyticsHiddenSeries', JSON.stringify(hiddenSeries));
    }, [hiddenSeries]);

    const exportAnalyticsCsv = () => {
        if (!analytics) return;

        const lines: string[] = [];
        lines.push('Metric,Value');
        lines.push(`Total Content,${analytics.totalContent}`);
        lines.push(`Active Campaigns,${analytics.activeCampaigns}`);
        lines.push(`Scheduled Campaigns,${analytics.scheduledCampaigns}`);
        lines.push(`Expired Campaigns,${analytics.expiredCampaigns}`);
        lines.push('');
        lines.push('Platform,Count');
        Object.entries(analytics.platformCounts).forEach(([platformKey, count]) => {
            lines.push(`${platformKey},${count}`);
        });
        lines.push('');
        lines.push('Campaign,Draft,Pending,Approved,Running,Paused,Stopped');
        Object.entries(analytics.campaignBreakdown).forEach(([campaign, statuses]) => {
            lines.push(
                `"${campaign.replace(/"/g, '""')}",${statuses.DRAFT || 0},${statuses.PENDING_APPROVAL || 0},${statuses.APPROVED || 0},${statuses.PUBLISHED || 0},${statuses.PAUSED || 0},${statuses.STOPPED || 0}`
            );
        });

        const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `social-media-analytics-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const topCampaigns = analytics
        ? Object.entries(analytics.campaignBreakdown)
              .map(([campaign, statuses]) => {
                  const running = statuses.PUBLISHED || 0;
                  const stopped = statuses.STOPPED || 0;
                  const totalLifecycle = running + stopped;
                  const retention = totalLifecycle > 0 ? (running / totalLifecycle) * 100 : 0;
                  return { campaign, running, stopped, retention };
              })
              .sort((a, b) => b.retention - a.retention || b.running - a.running)
              .slice(0, 5)
        : [];

    useEffect(() => {
        // Load content when component mounts
        loadContent();
        loadAnalytics();
        loadConnections();
    }, [loadContent, loadAnalytics, loadConnections]);

    const handleApprove = async (contentId: number) => {
        try {
            const response = await contentService.approveContent(contentId);
            if (response.success) {
                loadContent();
            } else {
                throw new Error(response.error || 'Failed to approve content');
            }
        } catch (err) {
            console.error('Error approving content:', err);
        }
    };

    const handleReject = async (contentId: number) => {
        const reason = prompt('Please enter a reason for rejection:');
        if (reason) {
            try {
                const response = await contentService.rejectContent(contentId, reason);
                if (response.success) {
                    loadContent();
                } else {
                    throw new Error(response.error || 'Failed to reject content');
                }
            } catch (err) {
                console.error('Error rejecting content:', err);
            }
        }
    };

    const handleDelete = async (contentId: number) => {
        if (window.confirm('Are you sure you want to delete this content?')) {
            try {
                const response = await contentService.deleteContent(contentId);
                if (response.success) {
                    loadContent();
                } else {
                    throw new Error(response.error || 'Failed to delete content');
                }
            } catch (err) {
                console.error('Error deleting content:', err);
            }
        }
    };

    const handlePublish = async (contentId: number) => {
        try {
            const response = await contentService.publishContent(contentId);
            if (response.success) {
                loadContent();
            } else {
                throw new Error(response.error || 'Failed to publish content');
            }
        } catch (err) {
            console.error('Error publishing content:', err);
        }
    };

    const handleStart = async (contentId: number) => {
        try {
            const response = await contentService.startCampaign(contentId);
            if (response.success) {
                loadContent();
            } else {
                throw new Error(response.error || 'Failed to start campaign');
            }
        } catch (err) {
            console.error('Error starting campaign:', err);
        }
    };

    const handlePause = async (contentId: number) => {
        try {
            const response = await contentService.pauseCampaign(contentId);
            if (response.success) {
                loadContent();
            } else {
                throw new Error(response.error || 'Failed to pause campaign');
            }
        } catch (err) {
            console.error('Error pausing campaign:', err);
        }
    };

    const handleStop = async (contentId: number) => {
        if (!window.confirm('Stop this campaign? This will end its active lifecycle.')) {
            return;
        }
        try {
            const response = await contentService.stopCampaign(contentId);
            if (response.success) {
                loadContent();
            } else {
                throw new Error(response.error || 'Failed to stop campaign');
            }
        } catch (err) {
            console.error('Error stopping campaign:', err);
        }
    };

    const handleRegisterCustomPlatform = async () => {
        const platformCode = newCustomPlatformCode.trim();
        const displayLabel = newCustomDisplayLabel.trim();
        const providerKey = newCustomProviderKey.trim();
        if (!platformCode || !providerKey) {
            alert('Platform code and provider key are required.');
            return;
        }
        const res = await contentService.registerConnectPlatform({
            platformCode,
            displayLabel: displayLabel || platformCode,
            providerKey
        });
        if (!res.success) {
            alert(res.error || 'Could not register platform');
            return;
        }
        setNewCustomPlatformCode('');
        setNewCustomDisplayLabel('');
        setNewCustomProviderKey('');
        await loadConnections();
        alert('Custom platform registered. Add external_provider_config rows for the provider key, then use Connect.');
    };

    const handleRemoveCustomPlatform = async (platformCode: string) => {
        if (!window.confirm(`Remove custom platform ${platformCode} from the registry?`)) return;
        const res = await contentService.removeConnectPlatform(platformCode);
        if (!res.success) {
            alert(res.error || 'Could not remove platform');
            return;
        }
        await loadConnections();
    };

    const handleConnectPlatform = async (platform: string) => {
        const response = await contentService.connectPlatform(platform);
        if (response.success && response.data?.authorizationUrl) {
            window.location.href = response.data.authorizationUrl;
        } else {
            alert(response.error || 'Could not initialize OAuth connect flow.');
        }
    };

    const handleDisconnectPlatform = async (platform: string) => {
        const response = await contentService.disconnectPlatform(platform);
        if (response.success) {
            loadConnections();
        } else {
            alert(response.error || 'Could not disconnect platform');
        }
    };

    const handlePublishModeSave = async (platform: string) => {
        if (publishModes[platform] === 'live') {
            const confirmed = window.confirm(
                `You are enabling LIVE publishing for ${platformDisplayLabel(platform)}. Real posts can be sent to the connected account. Continue?`
            );
            if (!confirmed) {
                return;
            }
        }
        const response = await contentService.setPlatformPublishMode(platform, publishModes[platform]);
        if (!response.success) {
            alert(response.error || 'Could not update publish mode');
            return;
        }
        alert(`${platformDisplayLabel(platform)} publish mode saved as "${publishModes[platform]}".`);
    };

    const platformFilterOptions =
        connectPlatformRows.length > 0
            ? connectPlatformRows
            : SOCIAL_CONNECT_PLATFORMS.map((p) => ({
                  platformCode: p,
                  displayLabel: SOCIAL_PLATFORM_LABELS[p],
                  providerKey: `social_${p.toLowerCase()}`,
                  builtin: true
              }));

    const loadPublishStatus = async (contentId: number) => {
        setPublishStatusLoadingId(contentId);
        setPublishStatusErrorByContent(prev => {
            const next = { ...prev };
            delete next[contentId];
            return next;
        });
        const response = await contentService.getPublishStatus(contentId);
        setPublishStatusLoadingId(null);
        if (response.success && response.data) {
            const d = response.data;
            const normalized: SocialPublishStatusResponse = {
                states: Array.isArray(d.states) ? d.states : [],
                jobs: Array.isArray(d.jobs) ? d.jobs : [],
                attempts: Array.isArray(d.attempts) ? d.attempts : []
            };
            setPublishStatusByContent(prev => ({ ...prev, [contentId]: normalized }));
        } else {
            setPublishStatusErrorByContent(prev => ({
                ...prev,
                [contentId]: response.error || 'Could not load publish telemetry.'
            }));
        }
    };

    const filteredContent = content.filter(item => {
        const matchesSearch = searchTerm === '' || 
            item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (item.tags && item.tags.toLowerCase().includes(searchTerm.toLowerCase()));
        
        const matchesPlatform = platform === 'all' || item.platform === platform;
        const matchesStatus = activeTab === 'all' || item.status.toLowerCase() === activeTab;
        
        return matchesSearch && matchesPlatform && matchesStatus;
    });

    if (loading) {
        return (
            <div className="admin-component-loading">
                <div className="loading-spinner"></div>
                <p>Loading content...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-component-error">
                <div className="error-icon">⚠️</div>
                <h3>Error Loading Content</h3>
                <p>{error}</p>
                <button className="retry-btn" onClick={loadContent}>
                    Try Again
                </button>
            </div>
        );
    }

    return (
        <div className="social-media-content-management">
            <div className="page-header">
                <h1 className="page-title">Social Media Content Management</h1>
                <p className="page-description">Review and manage social media content submitted by users</p>
                <div className="header-actions">
                    <button className="refresh-btn" onClick={() => { loadContent(); loadAnalytics(); }}>
                        <span>🔄</span>
                        Refresh
                    </button>
                </div>
            </div>

            <div className="filters-section">
                <div className="search-box">
                    <input
                        type="text"
                        placeholder="Search content..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="platform-filter">
                    <select value={platform} onChange={(e) => setPlatform(e.target.value)}>
                        <option value="all">All Platforms</option>
                        {platformFilterOptions.map((row) => (
                            <option key={row.platformCode} value={row.platformCode}>
                                {row.displayLabel}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="content-tabs">
                {[
                    { id: 'all', label: 'All', icon: '📋' },
                    { id: 'draft', label: 'Drafts', icon: '📝' },
                    { id: 'pending', label: 'Pending', icon: '⏳' },
                    { id: 'approved', label: 'Approved', icon: '✅' },
                    { id: 'published', label: 'Published', icon: '🌐' },
                    { id: 'paused', label: 'Paused', icon: '⏸️' },
                    { id: 'stopped', label: 'Stopped', icon: '⏹️' },
                    { id: 'analytics', label: 'Analytics', icon: '📊' },
                    { id: 'accounts', label: 'Accounts', icon: '🔐' }
                ].map(tab => (
                    <button
                        key={tab.id}
                        className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.id as typeof activeTab)}
                    >
                        <span className="tab-icon">{tab.icon}</span>
                        <span className="tab-label">{tab.label}</span>
                        <span className="tab-count">
                            ({tab.id === 'analytics' ? '-' : content.filter(item => 
                                tab.id === 'all' ? true : item.status.toLowerCase() === tab.id
                            ).length})
                        </span>
                    </button>
                ))}
            </div>

            {activeTab === 'analytics' && (
                <div className="content-table-section">
                    <div className="content-table-container" style={{ padding: '16px' }}>
                        <h3 style={{ marginBottom: '12px' }}>Campaign Performance Overview</h3>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
                            <select value={rangePreset} onChange={(e) => setRangePreset(e.target.value as '7d' | '30d' | 'custom')}>
                                <option value="7d">Last 7 days</option>
                                <option value="30d">Last 30 days</option>
                                <option value="custom">Custom range</option>
                            </select>
                            <select value={analyticsPlatform} onChange={(e) => setAnalyticsPlatform(e.target.value)}>
                                <option value="all">All Platforms</option>
                                {platformFilterOptions.map((row) => (
                                    <option key={row.platformCode} value={row.platformCode}>
                                        {row.displayLabel}
                                    </option>
                                ))}
                            </select>
                            {rangePreset === 'custom' && (
                                <>
                                    <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                                    <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                                </>
                            )}
                            <button className="refresh-btn" onClick={loadAnalytics}>Apply</button>
                            <button className="refresh-btn" onClick={exportAnalyticsCsv} disabled={!analytics}>Export CSV</button>
                        </div>
                        {analyticsLoading ? (
                            <p>Loading analytics...</p>
                        ) : !analytics ? (
                            <p>No analytics available yet.</p>
                        ) : (
                            <>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                                    <div className="bg-white border border-gray-200 rounded-lg p-3"><strong>Total Content:</strong> {analytics.totalContent}</div>
                                    <div className="bg-white border border-gray-200 rounded-lg p-3"><strong>Active:</strong> {analytics.activeCampaigns}</div>
                                    <div className="bg-white border border-gray-200 rounded-lg p-3"><strong>Scheduled:</strong> {analytics.scheduledCampaigns}</div>
                                    <div className="bg-white border border-gray-200 rounded-lg p-3"><strong>Expired:</strong> {analytics.expiredCampaigns}</div>
                                </div>

                                <h4 style={{ margin: '12px 0 8px' }}>By Platform</h4>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                                    {Object.entries(analytics.platformCounts).map(([platformKey, count]) => (
                                        <span key={platformKey} className="status-badge-table status-approved">{platformKey}: {count}</span>
                                    ))}
                                </div>

                                <h4 style={{ margin: '12px 0 8px' }}>Conversion Funnel</h4>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '16px' }}>
                                    {analytics.conversionFunnel?.map((step, index) => {
                                        const previous = index > 0 ? analytics.conversionFunnel[index - 1].count : step.count;
                                        const rate = index > 0 && previous > 0 ? ((step.count / previous) * 100).toFixed(1) : '100.0';
                                        return (
                                            <div key={step.stage} className="bg-white border border-gray-200 rounded-lg p-3">
                                                <div style={{ fontSize: '12px', color: '#6b7280' }}>{step.stage}</div>
                                                <div style={{ fontSize: '20px', fontWeight: 700 }}>{step.count}</div>
                                                <div style={{ fontSize: '12px', color: '#2563eb' }}>{rate}% from previous</div>
                                            </div>
                                        );
                                    })}
                                </div>

                                <h4 style={{ margin: '12px 0 8px' }}>Daily Campaign Trend</h4>
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                                    {[
                                        { key: 'created', label: 'Created', color: '#6b7280' },
                                        { key: 'approved', label: 'Approved', color: '#2563eb' },
                                        { key: 'running', label: 'Running', color: '#16a34a' },
                                        { key: 'paused', label: 'Paused', color: '#d97706' },
                                        { key: 'stopped', label: 'Stopped', color: '#dc2626' }
                                    ].map(series => (
                                        <button
                                            key={series.key}
                                            className="refresh-btn"
                                            onClick={() => setHiddenSeries(prev => ({ ...prev, [series.key]: !prev[series.key] }))}
                                            style={{
                                                opacity: hiddenSeries[series.key] ? 0.5 : 1,
                                                borderColor: series.color,
                                                color: series.color
                                            }}
                                        >
                                            {hiddenSeries[series.key] ? '☐' : '☑'} {series.label}
                                        </button>
                                    ))}
                                </div>
                                <div style={{ width: '100%', height: 320, marginBottom: '16px' }}>
                                    <ResponsiveContainer>
                                        <LineChart data={analytics.dailyTrend || []}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="date" />
                                            <YAxis allowDecimals={false} />
                                            <Tooltip />
                                            <Legend />
                                            <Line type="monotone" dataKey="created" stroke="#6b7280" name="Created" strokeWidth={2} hide={hiddenSeries.created} />
                                            <Line type="monotone" dataKey="approved" stroke="#2563eb" name="Approved" strokeWidth={2} hide={hiddenSeries.approved} />
                                            <Line type="monotone" dataKey="running" stroke="#16a34a" name="Running" strokeWidth={2} hide={hiddenSeries.running} />
                                            <Line type="monotone" dataKey="paused" stroke="#d97706" name="Paused" strokeWidth={2} hide={hiddenSeries.paused} />
                                            <Line type="monotone" dataKey="stopped" stroke="#dc2626" name="Stopped" strokeWidth={2} hide={hiddenSeries.stopped} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>

                                <h4 style={{ margin: '12px 0 8px' }}>Top Campaigns (Running Retention)</h4>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px', marginBottom: '16px' }}>
                                    {topCampaigns.length === 0 && <p>No campaign lifecycle data yet.</p>}
                                    {topCampaigns.map(item => (
                                        <div key={item.campaign} className="bg-white border border-gray-200 rounded-lg p-3">
                                            <div style={{ fontWeight: 700, marginBottom: '6px' }}>{item.campaign}</div>
                                            <div style={{ fontSize: '13px', color: '#374151' }}>Running: {item.running}</div>
                                            <div style={{ fontSize: '13px', color: '#374151' }}>Stopped: {item.stopped}</div>
                                            <div style={{ fontSize: '13px', color: '#166534', fontWeight: 700 }}>
                                                Retention: {item.retention.toFixed(1)}%
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <h4 style={{ margin: '12px 0 8px' }}>Campaign Breakdown</h4>
                                <table className="content-table">
                                    <thead>
                                        <tr>
                                            <th>Campaign</th>
                                            <th>Draft</th>
                                            <th>Pending</th>
                                            <th>Approved</th>
                                            <th>Running</th>
                                            <th>Paused</th>
                                            <th>Stopped</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Object.entries(analytics.campaignBreakdown).map(([campaign, statuses]) => (
                                            <tr key={campaign}>
                                                <td>{campaign}</td>
                                                <td>{statuses.DRAFT || 0}</td>
                                                <td>{statuses.PENDING_APPROVAL || 0}</td>
                                                <td>{statuses.APPROVED || 0}</td>
                                                <td>{statuses.PUBLISHED || 0}</td>
                                                <td>{statuses.PAUSED || 0}</td>
                                                <td>{statuses.STOPPED || 0}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'accounts' && (
                <div className="content-table-section">
                    <div className="content-table-container" style={{ padding: '16px' }}>
                        <h3 style={{ marginBottom: '12px' }}>Connected Social Accounts</h3>
                        <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
                            Built-in platforms use dedicated adapters. Custom rows use generic OAuth settings from{' '}
                            <code>external_provider_config</code> for the listed provider key.
                        </p>
                        <table className="content-table">
                            <thead>
                                <tr>
                                    <th>Platform</th>
                                    <th>Provider</th>
                                    <th>Status</th>
                                    <th>Account</th>
                                    <th>Token</th>
                                            <th>Publish Mode</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {platformFilterOptions.map((row) => {
                                    const platformKey = row.platformCode;
                                    const connection = connections.find(
                                        (c) => c.platform === platformKey || c.platform?.toUpperCase() === platformKey.toUpperCase()
                                    );
                                    const status = connection?.status || 'DISCONNECTED';
                                    const tokenExpiring = connection?.tokenExpiresAt
                                        ? new Date(connection.tokenExpiresAt).getTime() - Date.now() < 1000 * 60 * 60 * 24
                                        : false;
                                    return (
                                        <tr key={platformKey}>
                                            <td>
                                                <div>{row.displayLabel}</div>
                                                {!row.builtin && (
                                                    <div style={{ fontSize: '11px', color: '#9ca3af' }}>custom</div>
                                                )}
                                            </td>
                                            <td style={{ fontSize: '12px', color: '#4b5563' }}>{row.providerKey}</td>
                                            <td>
                                                <span className={`status-badge-table ${status === 'CONNECTED' ? 'status-approved' : status === 'ERROR' ? 'status-rejected' : 'status-draft'}`}>
                                                    {status === 'CONNECTED' && tokenExpiring ? 'Token Expiring' : status}
                                                </span>
                                            </td>
                                            <td>{connection?.accountName || connection?.accountId || '-'}</td>
                                            <td>{connection?.tokenExpiresAt ? new Date(connection.tokenExpiresAt).toLocaleString() : '-'}</td>
                                            <td>
                                                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                                    <select
                                                        value={publishModes[platformKey] ?? 'simulated'}
                                                        onChange={(e) =>
                                                            setPublishModes((prev) => ({
                                                                ...prev,
                                                                [platformKey]: e.target.value as 'simulated' | 'live'
                                                            }))
                                                        }
                                                    >
                                                        <option value="simulated">simulated</option>
                                                        <option value="live">live</option>
                                                    </select>
                                                    <button className="refresh-btn" onClick={() => handlePublishModeSave(platformKey)}>
                                                        Save
                                                    </button>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="table-actions" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                                                    {status === 'CONNECTED' ? (
                                                        <button type="button" className="delete-btn" onClick={() => handleDisconnectPlatform(platformKey)}>Disconnect</button>
                                                    ) : (
                                                        <button type="button" className="approve-btn" onClick={() => handleConnectPlatform(platformKey)}>Connect</button>
                                                    )}
                                                    {!row.builtin && (
                                                        <button
                                                            type="button"
                                                            className="delete-btn"
                                                            onClick={() => handleRemoveCustomPlatform(platformKey)}
                                                            title="Deactivate this platform in the registry"
                                                        >
                                                            Remove from list
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        <div
                            style={{
                                marginTop: '20px',
                                padding: '14px',
                                background: '#f9fafb',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb'
                            }}
                        >
                            <h4 style={{ margin: '0 0 8px 0' }}>Add custom platform</h4>
                            <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#6b7280' }}>
                                Use a short code (e.g. <strong>BLUESKY</strong>), a display name, and provider key{' '}
                                <strong>social_…</strong>. Then create matching keys in External Provider Config (auth_url, token_url,
                                client_id, client_secret, redirect_uri, scope, publish_mode).
                            </p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                                <input
                                    type="text"
                                    placeholder="Platform code (e.g. BLUESKY)"
                                    value={newCustomPlatformCode}
                                    onChange={(e) => setNewCustomPlatformCode(e.target.value)}
                                    style={{ minWidth: '160px', padding: '8px' }}
                                />
                                <input
                                    type="text"
                                    placeholder="Display label"
                                    value={newCustomDisplayLabel}
                                    onChange={(e) => setNewCustomDisplayLabel(e.target.value)}
                                    style={{ minWidth: '140px', padding: '8px' }}
                                />
                                <input
                                    type="text"
                                    placeholder="Provider key (e.g. social_bluesky)"
                                    value={newCustomProviderKey}
                                    onChange={(e) => setNewCustomProviderKey(e.target.value)}
                                    style={{ minWidth: '200px', padding: '8px' }}
                                />
                                <button type="button" className="approve-btn" onClick={() => void handleRegisterCustomPlatform()}>
                                    Register platform
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Content Table */}
            {activeTab !== 'analytics' && <div className="content-table-section">
                <div className="content-table-container">
                    <table className="content-table">
                        <thead>
                            <tr>
                                <th>Title</th>
                                <th>Platform</th>
                                <th>Type</th>
                                <th>Status</th>
                                <th>Submitted By</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredContent.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="no-content">
                                        <div className="no-content-message">
                                            <span>📝</span>
                                            <p>No content found matching your criteria.</p>
                                            <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                                                Content will appear here when users submit social media content for review.
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredContent.map(item => (
                                    <tr key={item.id} className="content-row">
                                        <td className="content-title">
                                            <div className="title-cell">
                                                <h4>{item.title}</h4>
                                                <p className="description">{item.description}</p>
                                                {item.tags && (
                                                    <div className="content-tags-table">
                                                        {item.tags.split(',').map(tag => (
                                                            <span key={tag.trim()} className="tag-table">
                                                                {tag.trim()}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="platform-cell">
                                            <span className="platform-badge-table">
                                                {item.platform}
                                            </span>
                                        </td>
                                        <td className="type-cell">
                                            <span className="type-badge">
                                                {item.contentType}
                                            </span>
                                        </td>
                                        <td className="status-cell">
                                            <span className={`status-badge-table status-${item.status.toLowerCase()}`}>
                                                {getStatusDisplay(item.status)}
                                            </span>
                                        </td>
                                        <td className="user-cell">
                                            <span className="user-badge">
                                                {item.createdBy ? `User ${item.createdBy}` : 'Unknown'}
                                            </span>
                                        </td>
                                        <td className="date-cell">
                                            {new Date(item.createdAt).toLocaleDateString()}
                                        </td>
                                        <td className="actions-cell">
                                            <div className="table-actions">
                                                <button
                                                    className="view-btn"
                                                    onClick={() => {
                                                        setSelectedContent(item);
                                                        setShowViewModal(true);
                                                    }}
                                                    title="View Content"
                                                >
                                                    👁️ View
                                                </button>
                                                <button
                                                    className="view-btn"
                                                    type="button"
                                                    disabled={publishStatusLoadingId === item.id}
                                                    onClick={() => loadPublishStatus(item.id)}
                                                    title="Load publish telemetry"
                                                >
                                                    {publishStatusLoadingId === item.id ? '⏳ Telemetry…' : '📡 Telemetry'}
                                                </button>
                                                {item.status === 'PENDING_APPROVAL' && (
                                                    <>
                                                        <button
                                                            className="approve-btn"
                                                            onClick={() => handleApprove(item.id)}
                                                            title="Approve Content"
                                                        >
                                                            ✅ Approve
                                                        </button>
                                                        <button
                                                            className="reject-btn"
                                                            onClick={() => handleReject(item.id)}
                                                            title="Reject Content"
                                                        >
                                                            ❌ Reject
                                                        </button>
                                                    </>
                                                )}
                                                {item.status === 'APPROVED' && (
                                                    <>
                                                        <button
                                                            className="approve-btn"
                                                            onClick={() => handlePublish(item.id)}
                                                            title="Publish now"
                                                        >
                                                            🌐 Publish Now
                                                        </button>
                                                        <button
                                                            className="approve-btn"
                                                            onClick={() => handleStart(item.id)}
                                                            title={item.publishDate && new Date(item.publishDate) > new Date() ? 'Schedule campaign at publish date' : 'Start campaign'}
                                                        >
                                                            {item.publishDate && new Date(item.publishDate) > new Date() ? '🕒 Schedule' : '▶️ Start'}
                                                        </button>
                                                    </>
                                                )}
                                                {item.status === 'PUBLISHED' && (
                                                    <>
                                                        <button
                                                            className="reject-btn"
                                                            onClick={() => handlePause(item.id)}
                                                            title="Pause Campaign"
                                                        >
                                                            ⏸️ Pause
                                                        </button>
                                                        <button
                                                            className="delete-btn"
                                                            onClick={() => handleStop(item.id)}
                                                            title="Stop Campaign"
                                                        >
                                                            ⏹️ Stop
                                                        </button>
                                                    </>
                                                )}
                                                {item.status === 'PAUSED' && (
                                                    <>
                                                        <button
                                                            className="approve-btn"
                                                            onClick={() => handleStart(item.id)}
                                                            title="Resume Campaign"
                                                        >
                                                            ▶️ Resume
                                                        </button>
                                                        <button
                                                            className="delete-btn"
                                                            onClick={() => handleStop(item.id)}
                                                            title="Stop Campaign"
                                                        >
                                                            ⏹️ Stop
                                                        </button>
                                                    </>
                                                )}
                                                <button
                                                    className="delete-btn"
                                                    onClick={() => handleDelete(item.id)}
                                                    title="Delete Content"
                                                >
                                                    🗑️ Delete
                                                </button>
                                            </div>
                                            {(publishStatusLoadingId === item.id ||
                                                publishStatusErrorByContent[item.id] ||
                                                publishStatusByContent[item.id]) && (
                                                <div
                                                    className="social-publish-telemetry"
                                                    style={{
                                                        marginTop: '10px',
                                                        padding: '10px 12px',
                                                        background: '#f9fafb',
                                                        borderRadius: '8px',
                                                        border: '1px solid #e5e7eb',
                                                        fontSize: '12px',
                                                        lineHeight: 1.45,
                                                        color: '#374151'
                                                    }}
                                                >
                                                    <div style={{ fontWeight: 600, marginBottom: '8px' }}>Publish telemetry</div>
                                                    {publishStatusLoadingId === item.id && (
                                                        <p style={{ margin: 0, color: '#6b7280' }}>Loading…</p>
                                                    )}
                                                    {publishStatusErrorByContent[item.id] && publishStatusLoadingId !== item.id && (
                                                        <p style={{ margin: 0, color: '#b91c1c' }} role="alert">
                                                            {publishStatusErrorByContent[item.id]}
                                                        </p>
                                                    )}
                                                    {publishStatusByContent[item.id] && publishStatusLoadingId !== item.id && (
                                                        <>
                                                            {(publishStatusByContent[item.id].states || []).length === 0 &&
                                                                (publishStatusByContent[item.id].jobs || []).length === 0 &&
                                                                (publishStatusByContent[item.id].attempts || []).length === 0 && (
                                                                    <p style={{ margin: '0 0 8px 0', color: '#6b7280' }}>
                                                                        No jobs, platform states, or attempt logs for this item yet. That is normal if you
                                                                        never ran <strong>Publish Now</strong> / <strong>Start</strong> after approval, or
                                                                        if publish tables are empty. After Flyway <strong>V121+</strong>, start a campaign
                                                                        again to populate telemetry.
                                                                    </p>
                                                                )}
                                                            {(publishStatusByContent[item.id].states || []).length > 0 && (
                                                                <div style={{ marginBottom: '8px' }}>
                                                                    <div style={{ fontWeight: 600, fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                                                                        Platform state
                                                                    </div>
                                                                    {(publishStatusByContent[item.id].states || []).map(state => (
                                                                        <div key={`${item.id}-${state.platform}`} style={{ marginBottom: '2px' }}>
                                                                            <strong>{state.platform}</strong>: {state.status}
                                                                            {state.lastError ? ` — ${state.lastError}` : ''}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            {(publishStatusByContent[item.id].jobs || []).length > 0 && (
                                                                <div style={{ marginBottom: '8px' }}>
                                                                    <div style={{ fontWeight: 600, fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                                                                        Jobs (newest first)
                                                                    </div>
                                                                    {(publishStatusByContent[item.id].jobs || []).slice(0, 5).map((job: any) => (
                                                                        <div key={job.id} style={{ marginBottom: '4px' }}>
                                                                            #{job.id} <strong>{job.status}</strong>
                                                                            {job.lastError ? ` — ${job.lastError}` : ''}
                                                                            {['FAILED', 'FAILED_PERMANENT'].includes(job.status) && (
                                                                                <button
                                                                                    type="button"
                                                                                    className="refresh-btn"
                                                                                    style={{ marginLeft: '8px' }}
                                                                                    onClick={() => contentService.retryPublishJob(job.id)}
                                                                                >
                                                                                    Retry
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            {(publishStatusByContent[item.id].attempts || []).length > 0 && (
                                                                <div>
                                                                    <div style={{ fontWeight: 600, fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                                                                        Recent attempts
                                                                    </div>
                                                                    {(publishStatusByContent[item.id].attempts || []).slice(0, 5).map(att => (
                                                                        <div key={att.id} style={{ marginBottom: '2px' }}>
                                                                            #{att.attemptNumber} {att.platform} <strong>{att.status}</strong>
                                                                            {att.errorMessage ? ` — ${att.errorMessage}` : ''}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>}

            {showViewModal && selectedContent && (
                <ViewContentModal
                    content={selectedContent}
                    onClose={() => {
                        setShowViewModal(false);
                        setSelectedContent(null);
                    }}
                />
            )}
        </div>
    );
};

const getStatusDisplay = (status: string): string => {
    switch (status) {
        case 'DRAFT':
            return 'Draft';
        case 'PENDING_APPROVAL':
            return 'Pending Approval';
        case 'APPROVED':
            return 'Approved';
        case 'PUBLISHED':
            return 'Running';
        case 'PAUSED':
            return 'Paused';
        case 'STOPPED':
            return 'Stopped';
        case 'REJECTED':
            return 'Rejected';
        default:
            return status;
    }
};

export default SocialMediaContentManagement; 